import { cn } from '@goaltree/ui/lib/utils'
import { useReducedMotion } from 'motion/react'
import { useEffect, useRef } from 'react'

const PROFILE = [0.45, 0.7, 0.9, 0.6, 1, 0.65, 0.85, 0.55, 0.4]
const IDLE = 0.15

// Mobile browsers (iOS Safari, Android Chrome) hand the mic to whichever capture opened last, so a
// second stream for the meter silences speech recognition. Only desktop reads a real level.
function canTapMic() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return false
  const ua = navigator.userAgent
  const mobile =
    /Android|iPad|iPhone|iPod|Mobile/i.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  return !mobile
}

// Only reads the mic when permission is already granted (speech recognition asked for it), so it
// never shows a second prompt; otherwise it pulses whenever `activity` changes (new words).
export function MicLevelMeter({
  active,
  activity,
  className,
  barClassName,
  height = 16,
}: {
  active: boolean
  activity?: string
  className?: string
  barClassName?: string
  height?: number
}) {
  const reduce = !!useReducedMotion()
  const barsRef = useRef<(HTMLSpanElement | null)[]>([])
  const energyRef = useRef(0)

  useEffect(() => {
    energyRef.current = 0.8
  }, [activity])

  useEffect(() => {
    const bars = barsRef.current
    if (!active || reduce) {
      bars.forEach((b, i) => {
        if (b) b.style.transform = `scaleY(${active ? PROFILE[i] * 0.6 : IDLE})`
      })
      return
    }
    let cancelled = false
    let raf = 0
    let stream: MediaStream | null = null
    let ctx: AudioContext | null = null
    let analyser: AnalyserNode | null = null
    const buf = new Float32Array(512)
    let smooth = 0

    const tick = (now: number) => {
      let level: number
      if (analyser) {
        analyser.getFloatTimeDomainData(buf)
        let sum = 0
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
        level = Math.min(1, Math.sqrt(sum / buf.length) * 6)
      } else {
        energyRef.current *= 0.94
        level = energyRef.current
      }
      smooth += (level - smooth) * (level > smooth ? 0.5 : 0.15)
      bars.forEach((b, i) => {
        if (!b) return
        const wobble = 0.75 + 0.25 * Math.sin(now / 90 + i * 1.7)
        b.style.transform = `scaleY(${Math.max(IDLE, Math.min(1, smooth * PROFILE[i] * wobble * 1.6))})`
      })
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    void (async () => {
      if (!canTapMic()) return
      try {
        const perm = await navigator.permissions?.query({ name: 'microphone' as PermissionName })
        if (perm && perm.state !== 'granted') return
        const s = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        stream = s
        ctx = new AudioContext()
        const a = ctx.createAnalyser()
        a.fftSize = 512
        ctx.createMediaStreamSource(s).connect(a)
        analyser = a
      } catch {}
    })()

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      stream?.getTracks().forEach((t) => t.stop())
      void ctx?.close().catch(() => {})
    }
  }, [active, reduce])

  return (
    <span aria-hidden className={cn('flex items-center gap-[3px]', className)} style={{ height }}>
      {PROFILE.map((_, i) => (
        <span
          key={i}
          ref={(el) => {
            barsRef.current[i] = el
          }}
          className={cn(
            'h-full w-[3px] origin-center rounded-full transition-colors',
            active ? 'bg-primary' : 'bg-muted-foreground/40',
            barClassName,
          )}
          style={{ transform: `scaleY(${IDLE})` }}
        />
      ))}
    </span>
  )
}
